import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionTag,
  getTextSelection,
  keyBinding,
  KeyBindingProps,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  NamedShortcut,
  PrimitiveSelection,
  ProsemirrorAttributes,
  Static,
  toggleMark,
} from '@remirror/core';
import { CoreIcon } from '@remirror/icons';
import React from 'react';
import { InputRule, markInputRule } from 'remirror';

import { WFormatTypeProperty } from './_interface';

const CREATE_COMMAND_LABEL = 'Toggle Subscript';

const toggleWFormatSubscriptOptions: Remirror.CommandDecoratorOptions = {
  icon: 'subscript2',
  label: ({ t }) => t(CREATE_COMMAND_LABEL),
  shortcut: NamedShortcut.Subscript,
};

export type WFormatSubscriptExtensionAttributes = ProsemirrorAttributes<{
  /**
   * The Mark type.
   */
  type?: WFormatTypeProperty;
}>;
export interface WFormatSubscriptOptions {
  /**
   * Require set the mark type property for this extension.
   */
  type?: Static<WFormatTypeProperty>;
  defaultType?: Static<WFormatTypeProperty>;
}

/**
 * When added to your editor it will provide the `toggleBold` command which makes the
 * text under the cursor / or at the provided position range bold.
 */
@extension<WFormatSubscriptOptions>({
  defaultOptions: {
    type: undefined,
    defaultType: undefined,
  },
  staticKeys: ['defaultType', 'type'],
})
export class WFormatSubscriptExtension extends MarkExtension<WFormatSubscriptOptions> {
  get name() {
    return 'wFormatSubscript' as const;
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra: ApplySchemaAttributes, override: MarkSpecOverride): MarkExtensionSpec {
    return {
      ...override,
      attrs: {
        ...extra.defaults(),
        type: { default: this.options.defaultType },
      },
      parseDOM: [
        {
          tag: `w-format[type=subscript]`,
          getAttrs: (element: string | Node) => ({
            ...extra.parse(element),
            type: 'subscript',
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark) => {
        const { type } = this.options;
        return ['w-format', { type: mark.attrs.type.toString() }, 0];
      },
    };
  }

  createInputRules(): InputRule[] {
    return [
      markInputRule({
        regexp: /(?:\*\*|__)([^*_]+)(?:\*\*|__)$/,
        type: this.type,
        ignoreWhitespace: true,
      }),
    ];
  }

  /**
   * Toggle the bold styling on and off. Remove the formatting if any
   * matching bold formatting within the selection or provided range.
   */

  @command(toggleWFormatSubscriptOptions)
  toggleWFormatSubscript(attrs?: WFormatSubscriptExtensionAttributes, selection?: PrimitiveSelection): CommandFunction {
    return toggleMark({ type: this.type, selection, attrs: { ...attrs, type: 'subscript' } });
  }

  /**
   * Set the bold formatting for the provided range.
   */
  @command()
  setWFormatSubscript(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      dispatch?.(tr.addMark(from, to, this.type.create()));

      return true;
    };
  }

  /**
   * Remove the bold formatting from the provided range.
   */
  @command()
  removeWFormatSubscript(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);

      if (!tr.doc.rangeHasMark(from, to, this.type)) {
        return false;
      }

      dispatch?.(tr.removeMark(from, to, this.type));

      return true;
    };
  }

  /**
   * Attach the keyboard shortcut for making text bold to this mark and also to
   * the `toggleBold` command.
   */
  @keyBinding({ shortcut: NamedShortcut.Subscript, command: 'toggleWFormatSubscript' })
  shortcut(props: KeyBindingProps): boolean {
    return this.toggleWFormatSubscript()(props);
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Remirror {
    interface AllExtensions {
      wFormatSubscript: WFormatSubscriptExtension;
    }
  }
}
